import { privateApiClient } from '../services/apiClient';
import { normalizePosMethod } from './operator';

/** Clé d'un numéro : ses 9 derniers chiffres (format RDC), pour rapprocher +243 81…, 081… et 81…. */
export const phoneKey = (zip, phone) => {
    const digits = `${zip || ''}${phone || ''}`.replace(/\D/g, '');
    return digits.length >= 9 ? digits.slice(-9) : digits || null;
};

export const isAnonymousName = (name) => !name || /^anonyme$/i.test(String(name).trim()) || /^client$/i.test(String(name).trim());

/**
 * Activité réelle des clients : paiements réussis (payés / encaissés) du pos-service,
 * regroupés par numéro de téléphone (ceux d'un caissier si `emitterId` est fourni).
 * @returns {Promise<Map<string, { count, totals: {CDF, USD}, lastAt: Date|null, payments: Array }>>}
 */
export const fetchClientInsights = async (orgId, emitterId = null) => {
    const insights = new Map();
    if (!orgId) return insights;
    const { data } = await privateApiClient.get('/pos/transaction-requests', {
        params: { org: orgId, pge: 1, lmt: 1000, tgt: 'all', sts: 'paid,cashed', slg: '', cmr: 'all', emt: 'all' },
    });
    // Le champ « emitter » de la demande n'est pas renseigné côté pos-service : on filtre sur le caissier responsable
    const requests = (data?.contents || []).filter((tr) => !emitterId || tr.accountable === emitterId);
    requests.forEach((tr) => {
        const method = normalizePosMethod(tr.method);
        const key = phoneKey(tr.client?.zip || method.prefix, tr.client?.phoneNumber || method.account);
        if (!key) return;
        const entry = insights.get(key) || { count: 0, totals: { CDF: 0, USD: 0 }, lastAt: null, payments: [] };
        const amount = Number(tr.amount) || 0;
        const currency = String(tr.currency || 'CDF').toUpperCase();
        const at = tr.paid_at || tr.create_at ? new Date(tr.paid_at || tr.create_at) : null;
        entry.count += 1;
        entry.totals[currency] = (entry.totals[currency] || 0) + amount;
        if (at && (!entry.lastAt || at > entry.lastAt)) entry.lastAt = at;
        entry.payments.push({ id: tr.id, reference: tr.trans_code || tr.code || tr.id, amount, currency, at, label: tr.label, target: tr.target });
        insights.set(key, entry);
    });
    insights.forEach((entry) => entry.payments.sort((a, b) => (b.at?.getTime() || 0) - (a.at?.getTime() || 0)));
    return insights;
};

/**
 * Fusionne les fiches d'un même numéro (doublons « Anonyme » créés par les paiements flash) :
 * une ligne par personne, la fiche nommée en priorité, avec l'activité réelle rattachée.
 */
export const groupClientsByPhone = (clients, insights = new Map()) => {
    const groups = new Map();
    clients.forEach((c) => {
        const key = phoneKey(c.zip, c.phoneNumber) || `id:${c.id}`;
        const group = groups.get(key) || { key, records: [] };
        group.records.push(c);
        groups.set(key, group);
    });
    return Array.from(groups.values()).map(({ key, records }) => {
        const primary = records.find((r) => !isAnonymousName(r.name)) || records[0];
        const activity = insights.get(key) || { count: 0, totals: { CDF: 0, USD: 0 }, lastAt: null, payments: [] };
        return {
            ...primary,
            key,
            anonymous: isAnonymousName(primary.name),
            email: primary.email || records.find((r) => r.email)?.email || '',
            duplicates: records.filter((r) => r.id !== primary.id),
            activity,
        };
    });
};
